// @flow

import { Component } from '../jsti/core';
import Cookies from '../constants/Cookies';
import Cookie from '../classes/Cookie';
import Notifications from '../constants/Notifications';
import Time from '../constants/Time';
import EventNames from '../constants/EventNames';
import AsyncRequest from '../classes/AsyncRequest';

interface LanguageConfiguration {
  key: string;
  locale: string;
}

interface Configuration {
  languages?: LanguageConfiguration[];
}

interface PotentialTranslation {
  language: string;
  title: string;
  text: string;
}

interface ButtonInformation {
  translation: string;
  link?: string;
}

interface ButtonTranslation {
  language: string;
  redirectButton: ButtonInformation;
  stayButton: ButtonInformation;
}

export default class DomainRedirectAlert extends Component {
  #configuration: Configuration | null = null;
  #potentialLanguageTranslations: PotentialTranslation[] = [];
  #potentialDomainTranslations: PotentialTranslation[] = [];
  #buttonTranslations: ButtonTranslation[] = [];
  #dismissHandler: function;

  /**
   * Instantiate the component
   */
  bind(el: HTMLElement): void {
    super.bind(el);

    try {
      this.#configuration = JSON.parse(this.el.dataset.configuration);
      this.#potentialLanguageTranslations = JSON.parse(this.el.dataset.potentialLanguageTranslations);
      this.#potentialDomainTranslations = JSON.parse(this.el.dataset.potentialDomainTranslations);
      this.#buttonTranslations = JSON.parse(this.el.dataset.buttonTranslations);
    } catch (error) {
    }

    if (!this.#configuration || Cookie.get(Cookies.DOMAIN_REDIRECT_ALERT) === '1') return;

    if (Cookie.get(Cookies.TRACKING) !== '') {
      this.propose();
    } else {
      this.addListener(Notifications.COOKIES_CHANGED, () => {
        setTimeout(() => {
          this.propose();
        }, 5 * Time.SECOND_IN_MILLISECONDS);
      });
    }
  }

  async propose(): void {
    const language = this.potentialPreferredLanguage();
    const domain: string | null = await this.potentialPreferredDomain();

    if (!language && !domain) {
      Cookie.set(Cookies.DOMAIN_REDIRECT_ALERT, 1, Time.YEAR_IN_SECONDS);
    } else {
      this.#dismissHandler = this.dismiss.bind(this);
      this.el.innerHTML = this.html(language, domain);
      this.dismissButtons.forEach((button: HTMLElement) => {
        button.addEventListener(EventNames.CLICK, this.#dismissHandler);
      });
    }
  }

  potentialPreferredLanguage(): string | null {
    if (!this.#configuration.languages) {
      return null;
    }

    return this.#configuration.languages.find((language: LanguageConfiguration) => window.navigator.language.startsWith(language.key))?.key || null;
  }

  async potentialPreferredDomain(): Promise<string | null> {
    return (await AsyncRequest.get('/domain-redirect')).data.potentialRedirect;
  }

  html(potentialPreferredLanguage: string | null, potentialPreferredDomain: string | null): string {
    const translation = potentialPreferredDomain
      ? this.#potentialDomainTranslations.find((translation: PotentialTranslation) => translation.language === potentialPreferredDomain)
      : this.#potentialLanguageTranslations.find((translation: PotentialTranslation) => translation.language === potentialPreferredLanguage);

    const buttonTranslation = this.#buttonTranslations.find((translation: ButtonTranslation) => translation.language === (potentialPreferredDomain || potentialPreferredLanguage));

    if (!translation || !buttonTranslation) {
      throw new Error(`Translations are not available for the ${potentialPreferredDomain || potentialPreferredLanguage} domain redirect alert`);
    }

    let stayDomain = `medpets.${window.store.environment.country.toLowerCase()}`;
    let redirectDomain = `medpets.${potentialPreferredDomain || potentialPreferredLanguage}`;
    if (window.store.environment.country.toLowerCase() === 'gb') {
      stayDomain = 'vetsend.co.uk';
    }
    if (potentialPreferredLanguage === 'uk' || potentialPreferredDomain === 'uk') {
      redirectDomain = 'vetsend.co.uk';
    }

    return `
      <div
        class="popup-modal popup-modal--show"
        data-component="popup-modal"
      >
        <div
          class="popup-modal__bg-blocker"
          ref="domain-redirect-alert-button"
        ></div>

        <div class="popup-modal__inner popup-modal__inner--center p-6 md:p-8 rounded max-w-lg rounded-xl">
          <span class="text-lg md:text-xl text-primary font-bold mb-5 block text-center mb-3">
            ${translation.title}
          </span>

          ${translation.text}

          <a
            href="${buttonTranslation.redirectButton.link || `https://${redirectDomain}`}"
            class="btn btn-tertiary mt-3 md:text-base"
          >
            ${buttonTranslation.redirectButton.translation.replace(':domain', redirectDomain)}
          </a>

          <button
            class="btn btn-outline mt-2.5 md:mt-5 w-full md:text-base"
            ref="domain-redirect-alert-button"
          >
            ${buttonTranslation.stayButton.translation.replace(':domain', stayDomain)}
          </button>
        </div>
      </div>
    `;
  }

  dismiss(): void {
    Cookie.set(Cookies.DOMAIN_REDIRECT_ALERT, 1, Time.YEAR_IN_SECONDS);
    this.removeEventListeners();
    this.el.innerHTML = '';
  }

  destroy() {
    this.removeEventListeners();
    super.destroy();
  }

  removeEventListeners(): void {
    if (!this.#dismissHandler) return;
    this.dismissButtons.forEach((button: HTMLElement) => {
      button.removeEventListener(EventNames.CLICK, this.#dismissHandler);
    });
  }

  get dismissButtons(): HTMLElement[] {
    return [...this.el.getElementsByRef('domain-redirect-alert-button')];
  }
}
